import React, { useState } from 'react';
import { Link } from '@/components/LocaleLink';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { cn } from '@/lib/utils';
import useScrollReveal from '@/hooks/useScrollReveal';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { contentCopy } from '@/contexts/language/translations/pages/content';

type ContentType = 'all' | 'case-study' | 'blog';

// Newest first — add each new case study / blog post at the top of this list.
// Card copy (EN + SK) lives in src/contexts/language/translations/pages/content.ts, keyed by slug.
const CONTENT_ITEMS = [
  { type: 'blog' as const, slug: '/blog/the-problem-with-ecommerce' },
  { type: 'case-study' as const, slug: '/case-studies/altisport' },
  { type: 'case-study' as const, slug: '/case-studies/herohero' },
  { type: 'case-study' as const, slug: '/case-studies/alasans' },
  { type: 'case-study' as const, slug: '/case-studies/dcm' },
];

const TYPE_STYLES: Record<string, string> = {
  'case-study': 'bg-[#E0E7FF] text-[#2A1E47]',
  blog: 'bg-[#FFF7EC] text-[#FF6B35]',
};

const Content = () => {
  const [activeFilter, setActiveFilter] = useState<ContentType>('all');
  useScrollReveal();
  const { language } = useLanguage();
  const c = contentCopy[language];

  const filtered = activeFilter === 'all'
    ? CONTENT_ITEMS
    : CONTENT_ITEMS.filter(item => item.type === activeFilter);

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={c.seoTitle}
        description={c.seoDescription}
        canonical="/content"
      />
      <Navigation />
      <main className="flex-1">

        {/* Hero */}
        <section className="relative pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#E0E7FF] rounded-full filter blur-3xl opacity-25" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FFF7EC] rounded-full filter blur-3xl opacity-50" />
          <div className="container-custom relative z-10 reveal">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-5">{c.hero.eyebrow}</p>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#2A1E47] mb-5 leading-tight">
                {c.hero.title}
              </h1>
              <p className="text-lg md:text-xl text-[#2A1E47]/70 max-w-xl mx-auto">
                {c.hero.subtitle}
              </p>
            </div>
          </div>
        </section>

        {/* Filter + Grid */}
        <section className="py-12 md:py-20 bg-[#FAFAFA]">
          <div className="container-custom">

            {/* Filter tabs */}
            <div className="flex items-center gap-2 mb-10 flex-wrap reveal">
              {(['all', 'case-study', 'blog'] as ContentType[]).map(type => (
                <button
                  key={type}
                  onClick={() => setActiveFilter(type)}
                  className={cn(
                    'px-5 py-2 rounded-full text-sm font-semibold border-2 transition-all duration-200',
                    activeFilter === type
                      ? 'bg-[#2A1E47] text-white border-[#2A1E47]'
                      : 'bg-white text-[#2A1E47]/60 border-gray-200 hover:border-[#2A1E47]/30 hover:text-[#2A1E47]'
                  )}
                >
                  {c.typeLabels[type]}
                  <span className="ml-2 text-xs opacity-60">
                    {type === 'all'
                      ? CONTENT_ITEMS.length
                      : CONTENT_ITEMS.filter(i => i.type === type).length}
                  </span>
                </button>
              ))}
            </div>

            {/* Content grid */}
            {filtered.length > 0 ? (
              <div className="grid gap-6 md:grid-cols-2 reveal">
                {filtered.map(({ type, slug }) => {
                  const item = { type, slug, ...c.items[slug] };
                  return (
                  <Link
                    key={item.slug}
                    to={item.slug}
                    className="group bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                  >
                    {/* Type badge + year */}
                    <div className="flex items-center justify-between mb-5">
                      <span className={cn('text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full', TYPE_STYLES[item.type])}>
                        {c.typeLabels[item.type]}
                      </span>
                      <span className="text-xs text-muted-foreground font-medium">{item.year}</span>
                    </div>

                    {/* Client */}
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6B35] mb-2">
                      {item.client} · {item.industry}
                    </p>

                    {/* Title */}
                    <h2 className="text-xl font-bold text-[#2A1E47] mb-3 leading-snug group-hover:text-[#FF6B35] transition-colors">
                      {item.title}
                    </h2>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">
                      {item.description}
                    </p>

                    {/* Stats */}
                    {item.stats && (
                      <div className="flex flex-wrap gap-3 mb-5">
                        {item.stats.map(stat => (
                          <span key={stat} className="text-xs font-semibold bg-[#F5F5F5] text-[#2A1E47]/70 px-3 py-1 rounded-full">
                            {stat}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-5">
                      {item.tags.map(tag => (
                        <span key={tag} className="text-xs text-[#2A1E47]/50 bg-gray-50 border border-gray-100 px-2.5 py-0.5 rounded-md">
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* CTA */}
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-[#2A1E47] group-hover:text-[#FF6B35] transition-colors mt-auto">
                      {item.type === 'blog' ? c.readArticle : c.readCaseStudy}
                      <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </div>
                  </Link>
                  );
                })}
              </div>
            ) : (
              <div className="py-20 text-center">
                <p className="text-xl text-muted-foreground">{c.empty}</p>
              </div>
            )}
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default Content;
